/**
 * Fetch up to N posts for a Featured Carousel tab sourced from
 * `template_components` (Latest / Series) — not stayuptodate / platform highlights.
 */

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
// Session-free client: public homepage strip RPCs must not race GoTrue/getSession
// (AbortError). Same API key as auth client — read results unchanged.
import { supabasePublic as supabase } from "../../../config/supabaseClient";
import { StaticVariables } from "../../../Utils/StaticVariables";
import { buildPhraseIlikePatterns } from "../../../components/SearchInput/phraseSearchHelpers";
import {
  getFirstUsableImageUrlForCard,
  pickFirstNonEmptyImageField,
} from "../../../Utils/StayUpToDate/RenderComponent";
import {
  deriveAllowedPlatforms,
  deriveShowPlatforms,
  filterOutProductCatalogPosts,
  filterPosts,
  readPlatformFilterFromDetails,
  shouldUseShopKeywordRpc,
  type ShowFilter,
} from "../LandingPageCarousels/QueryBasedCarousel/filterUtils";
import {
  filterRowsByRequestedFeedTypes,
  mapReelsFilterToFeedTypes,
} from "../../../Utils/helpers/carouselHelper";
import type { CarouselData, FeaturedCarouselItem } from "./types";
import {
  hasLikelyVideoMedia,
  isLikelyVideoUrl,
  resolvePlayableVideoUrl,
  splitMediaUrls,
} from "../../../Utils/media/feedMedia";
import {
  displayFeedCardTitle,
  isInstagramHandleLikeTitle,
} from "../../../Utils/media/feedCardTitle";
import { splitTemplateMediaUrls } from "./templatePostMedia";
import {
  buildTrendingDetails,
  loadTrendingContentEditorPostIds,
  resolveTrendingAnalyticsDates,
  trendingContentHydrateIds,
  trendingContentPostsForRange,
} from "./websiteEditorTrendingDetails";
import { filterPostsByShoppableStatus } from "./websiteEditorShoppableFilter";
import {
  fetchShopLinkMap,
  filterPostsWithShopLinks,
} from "../../../Utils/helpers/shoppableContent";
import { computeWindow } from "../../../Utils/customHooks/UpToDateContent/useLoadYesterdayLinks";
import { getOrComputeTrendingPostIds } from "../../../Utils/customHooks/UpToDateContent/trendingDailySnapshot";
import {
  getContentFeedDateWindowFromComponent,
  type ContentFeedDateWindow,
} from "../StayUpToDate/AddComponentPicker/contentFeedTimeframe";
import {
  CONTENT_FEED_ALL_FEED_TYPES,
  CONTENT_FEED_ALL_SOCIALS,
} from "../StayUpToDate/AddComponentPicker/contentFeedSelection";
import {
  getProductDayWiseShoplinkWindow,
  parseDayWiseSelectedDays,
} from "../StayUpToDate/AddComponentPicker/productFeedSelection";
import {
  normalizeFeedOrderType,
  sortFeedRowsByPostDate,
} from "./feedOrderUtils";
import {
  getProductImageSrc,
  tagTrendingProductsAsProductType,
} from "../../../Utils/productImage/productImageDisplay";
import { resolveShopButtonCta, normalizeShopUrl } from "../../../Utils/normalizeShopUrl";
import { parseProductBreadcrumbs } from "./shopBreadcrumbs";
import { isLookTextPlatform } from "./contentLooks";

dayjs.extend(utc);

const PRODUCT_PLATFORM_BRAND: Record<string, string> = {
  amazon: "Amazon",
  ltk: "LTK",
  shopmy: "ShopMy",
};

/** Brand under product tiles — breadcrumbs first, else platform label. */
function productBrandFromRow(row: Record<string, unknown>): string {
  const parsed = parseProductBreadcrumbs(String(row.breadcrumbs ?? "").trim());
  const fromBc = String(parsed?.brand ?? "").trim();
  if (fromBc) return fromBc;
  const platform = String(row.platform ?? "").trim();
  if (!platform) return "";
  const lc = platform.toLowerCase();
  if (lc in PRODUCT_PLATFORM_BRAND) return PRODUCT_PLATFORM_BRAND[lc];
  if (lc === "misc" || lc === "misc-content" || lc === "misc_content") return "";
  return platform.charAt(0).toUpperCase() + platform.slice(1);
}

export const FEATURED_CAROUSEL_POST_LIMIT = 25;

/**
 * Outbound URL for a post card / podcast episode — same priority as LiB
 * `redirectToUrl` / PostCard (`permalink || shop_link || table_url`).
 */
function resolvePostOutboundUrl(row: Record<string, unknown>): string {
  const keys = [
    "permalink",
    "url",
    "external_link",
    "shop_link",
    "table_url",
    "tagged_link",
    "link",
    "episode_url",
  ] as const;
  for (const key of keys) {
    const normalized = normalizeShopUrl(row[key]);
    if (normalized) return normalized;
    const raw = String(row[key] ?? "").trim();
    if (
      raw &&
      (raw.startsWith("http://") ||
        raw.startsWith("https://") ||
        raw.startsWith("spotify:"))
    ) {
      return raw;
    }
  }
  // Some podcast rows only store the Spotify episode page in media_url.
  const media = String(row.media_url ?? "").trim();
  if (
    media &&
    /(?:open\.)?spotify\.com\/(?:episode|show)|podcasts\.apple\.com|pca\.st\//i.test(
      media,
    )
  ) {
    return media;
  }
  return "";
}

const TYPE_TO_PLATFORM_FEED: Record<
  string,
  { platform: string; feedType: string }
> = {
  tiktok: { platform: "tiktok", feedType: "TIKTOK" },
  twitter: { platform: "twitter", feedType: "TWEET" },
  youtube: { platform: "youtube", feedType: "VIDEO" },
  podcast: { platform: "podcast", feedType: "PODCAST" },
  linkedin: { platform: "linkedin", feedType: "LINKEDIN" },
  reels: { platform: "instagram", feedType: "REELS" },
  "instagram story": { platform: "instagram", feedType: "STORY" },
  all: { platform: "all", feedType: "ALL" },
};

function rowPostId(raw: unknown): string {
  if (!raw || typeof raw !== "object") return "";
  const row = raw as { post_id?: unknown; id?: unknown };
  return String(row.post_id ?? row.id ?? "").trim();
}

/** Custom order is the carousel sequence — same list the phrase picker saves. */
function sortRowsByCustomOrder(posts: unknown[], customOrder: string[]): unknown[] {
  if (!customOrder.length) return posts;
  const rank = new Map(customOrder.map((id, index) => [id, index]));
  return [...posts].sort((a, b) => {
    const ia = rank.has(rowPostId(a)) ? rank.get(rowPostId(a))! : Number.MAX_SAFE_INTEGER;
    const ib = rank.has(rowPostId(b)) ? rank.get(rowPostId(b))! : Number.MAX_SAFE_INTEGER;
    return ia - ib;
  });
}

function parseIdList(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.map((v) => String(v).trim()).filter(Boolean);
  }
  if (typeof raw === "string" && raw.trim()) {
    return raw.split(",").map((s) => s.trim()).filter(Boolean);
  }
  return [];
}

/** Normalize DB / UI content_order into ASC | DESC | CUSTOM for RPCs. */
export { normalizeFeedOrderType, sortFeedRowsByPostDate } from "./feedOrderUtils";

function asShowFilter(raw?: string, fallback: ShowFilter = "products"): ShowFilter {
  const f = String(raw || fallback).trim().toLowerCase();
  if (f === "content") return "content";
  if (f === "both") return "both";
  if (f === "products") return "products";
  return fallback;
}

function hasProductFeedKind(details?: string): boolean {
  const raw = String(details || "").trim();
  if (!raw.startsWith("{")) return false;
  try {
    const parsed = JSON.parse(raw) as { feedKind?: string };
    return parsed?.feedKind === "product-feed";
  } catch {
    return false;
  }
}

/**
 * Platform content-feed Add writes type reels/all/tiktok/… — never product-scope
 * by title or by Show=content. Product Latest uses feedKind or filter=products.
 */
const PLATFORM_CONTENT_FEED_TYPES = new Set([
  "reels",
  "all",
  "tiktok",
  "youtube",
  "twitter",
  "linkedin",
  "podcast",
  "instagram story",
  "latest-podcast",
  "featured",
]);

function isPlatformContentFeedType(typeLc: string): boolean {
  return PLATFORM_CONTENT_FEED_TYPES.has(String(typeLc || "").trim().toLowerCase());
}

/** Latest Product Feed uses type `reels` — don't fetch it as Instagram Posts & Reels. */
function isProductScopeLatest(carousel: CarouselData): boolean {
  const typeLc = String(carousel.componentType || "").trim().toLowerCase();
  // Platform "All" and other non-reels Latest feeds are never product-scope.
  if (typeLc === "all") return false;
  if (hasProductFeedKind(carousel.details)) {
    // Explicit product-feed Add (even when type is reels).
    return typeLc === "reels" || typeLc === "";
  }
  if (typeLc !== "reels" && typeLc !== "") return false;
  const f = String(carousel.shoppableContentFilter || "").trim().toLowerCase();
  // Only Show=products is product-scope. Show=content / both / post / reels are
  // content-feed Instagram filters — never force PRODUCT platform rows.
  return f === "products";
}

/** Drop ShopMy/LTK PRODUCT rows from content strips (Show ≠ products). */
function excludeProductTypeRows(
  posts: unknown[],
  shoppableContentFilter?: string,
): unknown[] {
  const f = String(shoppableContentFilter || "")
    .trim()
    .toLowerCase();
  if (f === "products") return posts;
  return posts.filter((raw) => {
    if (!raw || typeof raw !== "object") return false;
    const t = String((raw as { type?: unknown }).type ?? "")
      .trim()
      .toUpperCase();
    return t !== "PRODUCT";
  });
}

function isVideoPost(row: Record<string, unknown>): boolean {
  const type = String(row.type ?? "").trim();
  if (type.toUpperCase() === "PODCAST") return false;
  const platform = String(row.platform ?? "").trim();
  if (platform.toLowerCase() === "podcast") return false;
  return hasLikelyVideoMedia(
    String(row.media_url ?? ""),
    type,
    platform,
    String(row.storage_url ?? ""),
  );
}

/**
 * Playable media URL for hover-to-play (same source Recent Content video tiles use).
 */
function postMediaUrl(row: Record<string, unknown>): string {
  return resolvePlayableVideoUrl({
    mediaUrl: String(row.media_url ?? "").trim(),
    storageUrl: String(row.storage_url ?? "").trim(),
    type: String(row.type ?? "").trim(),
    platform: String(row.platform ?? "").trim(),
  });
}

/**
 * Poster / cover for an `<img>` card — never use a video file URL.
 * TikTok / Reels / YouTube use `thumbnail` (or thumbnail_url); `media_url` /
 * `storage_url` are often the playable video and break as image srcs.
 * PRODUCT rows prefer `product_image` (same priority as LiB product-image memo).
 */
function postImage(row: Record<string, unknown>): string {
  const postType = String(row.type ?? "").trim().toUpperCase();
  if (postType === "PRODUCT") {
    const productSrc = getProductImageSrc({
      type: "PRODUCT",
      product_image: pickFirstNonEmptyImageField(row.product_image),
      table_image: pickFirstNonEmptyImageField(row.table_image),
      media_url: pickFirstNonEmptyImageField(row.media_url),
    });
    if (productSrc && !isLikelyVideoUrl(productSrc)) return productSrc;
  }

  const meta =
    row.metadata && typeof row.metadata === "object"
      ? (row.metadata as Record<string, unknown>)
      : null;

  const thumbs = [
    pickFirstNonEmptyImageField(row.thumbnail),
    pickFirstNonEmptyImageField(row.thumbnail_url),
    pickFirstNonEmptyImageField(meta?.ep_thumbnail),
    pickFirstNonEmptyImageField(meta?.thumbnail),
  ].filter(Boolean) as string[];
  for (const t of thumbs) {
    if (!isLikelyVideoUrl(t)) return t;
  }

  const fromCard = getFirstUsableImageUrlForCard(row);
  if (fromCard && !isLikelyVideoUrl(fromCard)) return fromCard;

  const fallbacks = [
    pickFirstNonEmptyImageField(row.product_image),
    pickFirstNonEmptyImageField(row.table_image),
    pickFirstNonEmptyImageField(row.card_image),
    pickFirstNonEmptyImageField(row.image),
    pickFirstNonEmptyImageField(row.storage_url),
    pickFirstNonEmptyImageField(row.media_url),
  ].filter(Boolean) as string[];
  for (const u of fallbacks) {
    if (!isLikelyVideoUrl(u)) return u;
  }

  return thumbs[0] || "";
}

/** Product / shop carousels — keep for callers that need product scope, but do
 *  not force preview shoppable filtering (destination honors DB filter). */
function isProductShoppableOnlyCarousel(carousel: CarouselData): boolean {
  if (isProductScopeLatest(carousel)) return true;
  if (carousel.type === "shop") return true;
  const typeLc = String(carousel.componentType || "")
    .trim()
    .toLowerCase();
  // Content Keywords stay content-scoped even with a stale products filter.
  if (typeLc === "query-content" || typeLc === "content-series") {
    return false;
  }
  if (
    typeLc === "shoppable" ||
    typeLc === "links" ||
    typeLc === "query-links" ||
    typeLc === "trending products"
  ) {
    return true;
  }
  const f = String(carousel.shoppableContentFilter || "")
    .trim()
    .toLowerCase();
  return f === "products";
}

/** Same shoppable_status_filter the destination loaders read from the component.
 *  Podcast never uses shoppable filtering — leftover values from another platform
 *  must not empty the strip preview. */
function resolvePreviewShoppableFilter(carousel: CarouselData): string {
  const typeLc = String(carousel.componentType || "")
    .trim()
    .toLowerCase();
  if (typeLc === "podcast" || typeLc === "latest-podcast") return "all";
  return String(carousel.shoppableStatusFilter || "all").trim().toLowerCase() || "all";
}

function parseRemovedPostIds(carousel: CarouselData): string[] {
  return parseIdList(carousel.removedPostIds);
}

/** Drop posts the creator removed from the tab — same as destination loaders. */
function excludeRemovedPosts(
  posts: unknown[],
  removedPostIds: string[],
): unknown[] {
  if (!removedPostIds.length) return posts;
  const ban = new Set(removedPostIds.map(String).map((s) => s.trim()).filter(Boolean));
  if (!ban.size) return posts;
  return posts.filter((raw) => {
    if (!raw || typeof raw !== "object") return false;
    const id = String(
      (raw as { post_id?: unknown; id?: unknown }).post_id ??
        (raw as { id?: unknown }).id ??
        "",
    ).trim();
    return Boolean(id) && !ban.has(id);
  });
}

function postTitle(
  row: Record<string, unknown>,
  opts?: { isProduct?: boolean },
): string {
  const raw = opts?.isProduct
    ? String(
        row.product_name ??
          row.shop_title ??
          row.content_title ??
          row.caption ??
          row.title ??
          row.name ??
          "",
      ).trim()
    : String(row.caption ?? row.title ?? row.name ?? "").trim();
  if (!raw) return "";
  const first = raw.split("\n")[0]?.slice(0, 80) || "";
  if (isInstagramHandleLikeTitle(first, row)) {
    return displayFeedCardTitle(row);
  }
  return first;
}

function mapPosts(
  posts: unknown[],
  tabSlug: string,
  limit: number,
  opts?: {
    forceProduct?: boolean;
    kind?: "shop" | "content";
    /** Homepage strip requires paintable thumbs (ghost preview skips imageless). */
    requireImage?: boolean;
  },
): FeaturedCarouselItem[] {
  const tagged = opts?.forceProduct
    ? tagTrendingProductsAsProductType(
        posts as { type?: string | null }[],
        "trending products",
      )
    : posts;
  const out: FeaturedCarouselItem[] = [];
  const seen = new Set<string>();
  for (const raw of tagged) {
    if (!raw || typeof raw !== "object") continue;
    const row = raw as Record<string, unknown>;
    const id = String(row.post_id ?? row.id ?? "").trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const postType = String(row.type ?? "").trim();
    const isProduct = postType.toUpperCase() === "PRODUCT" || opts?.forceProduct;
    const image = postImage(row);
    const platform = String(row.platform ?? "").trim();
    const isText =
      !isProduct &&
      !String(image || "").trim() &&
      isLookTextPlatform(platform, postType);
    const playable = postMediaUrl(row);
    if (
      opts?.requireImage &&
      !String(image || "").trim() &&
      !isText &&
      !playable
    ) {
      continue;
    }
    const productImage = pickFirstNonEmptyImageField(row.product_image) || undefined;
    const mediaUrl = postMediaUrl(row);
    const mediaUrls = splitTemplateMediaUrls(String(row.media_url ?? ""));
    const isVideo = !isProduct && isVideoPost(row);
    const carouselMediaUrls = !isProduct
      ? splitMediaUrls(String(row.media_url ?? "")).slice(0, 10)
      : [];
    const carouselThumbnails = !isProduct
      ? splitMediaUrls(String(row.thumbnail ?? ""))
      : [];
    const shopCta = resolveShopButtonCta(row);
    const shopLink = shopCta?.url || undefined;
    const shopCtaLabel = shopCta?.label;
    const permalink = resolvePostOutboundUrl(row) || undefined;
    const brand = isProduct ? productBrandFromRow(row) : "";
    const postDate = String(row.post_date ?? "").trim() || undefined;
    out.push({
      id,
      title: postTitle(row, { isProduct }),
      image: image || "",
      productImage,
      postType: isProduct ? "PRODUCT" : postType || undefined,
      mediaUrl: isVideo ? mediaUrl : undefined,
      ...(mediaUrls.length > 1
        ? {
            mediaUrls,
            thumbnailUrls: splitTemplateMediaUrls(String(row.thumbnail ?? "")),
          }
        : {}),
      isVideo: isVideo || undefined,
      ...(carouselMediaUrls.length > 1
        ? {
            carouselMediaUrls,
            ...(carouselThumbnails.length
              ? { carouselThumbnails }
              : {}),
          }
        : {}),
      slug: tabSlug,
      kind: opts?.kind || (isProduct ? "shop" : "content"),
      ...(brand ? { brand } : {}),
      ...(shopLink ? { shopLink } : {}),
      ...(shopCtaLabel ? { shopCtaLabel } : {}),
      ...(permalink ? { permalink } : {}),
      ...(platform ? { platform } : {}),
      ...(isText ? { isText: true } : {}),
      ...(postDate ? { postDate } : {}),
    });
    if (out.length >= limit) break;
  }
  return out;
}

function isPostRow(value: unknown): boolean {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const row = value as Record<string, unknown>;
  return (
    "post_id" in row ||
    "media_url" in row ||
    "thumbnail" in row ||
    "platform" in row ||
    "caption" in row
  );
}

function rpcRows(data: unknown, depth = 0): unknown[] {
  if (depth > 4) return [];
  if (typeof data === "string") {
    const trimmed = data.trim();
    if (!trimmed) return [];
    try {
      return rpcRows(JSON.parse(trimmed), depth + 1);
    } catch {
      return [];
    }
  }
  if (Array.isArray(data)) {
    if (
      data.length === 1 &&
      !isPostRow(data[0]) &&
      data[0] &&
      typeof data[0] === "object" &&
      "data" in (data[0] as object)
    ) {
      return rpcRows((data[0] as { data?: unknown }).data, depth + 1);
    }
    return data;
  }
  if (data && typeof data === "object" && "data" in (data as object)) {
    return rpcRows((data as { data?: unknown }).data, depth + 1);
  }
  return [];
}

async function fetchPostsByIds(args: {
  userId: string;
  username: string;
  postIds: string[];
  orderType?: string;
  customOrder?: string[] | null;
  limit: number;
  removedPostIds?: string[];
  /** Match useLoadCustomContent: products → PRODUCT_PLATFORMS. */
  shoppableContentFilter?: string;
  /** When true (Custom Products / links), default to product platforms. */
  preferProductPlatforms?: boolean;
}): Promise<unknown[]> {
  const {
    userId,
    username,
    postIds,
    orderType,
    customOrder,
    limit,
    removedPostIds = [],
    shoppableContentFilter,
    preferProductPlatforms,
  } = args;
  const ids = Array.from(new Set(postIds.map(String).map((s) => s.trim())))
    .filter(Boolean)
    .slice(0, Math.max(limit * 2, limit));
  if (!userId || !username || ids.length === 0) return [];

  const sv = StaticVariables();
  const filterLc = String(shoppableContentFilter || "")
    .trim()
    .toLowerCase();
  // Same platform pick as useLoadCustomContent / fetchCustomOrderedFirst.
  let platforms = sv.PLATFORMS as string[];
  if (filterLc === "content") {
    platforms = sv.CONTENT_PLATFORMS as string[];
  } else if (
    filterLc === "products" ||
    (preferProductPlatforms && filterLc !== "both")
  ) {
    platforms = sv.PRODUCT_PLATFORMS as string[];
  }

  // Only pass custom_order for CUSTOM — otherwise ASC/DESC sort by date is ignored.
  const normalizedOrder = String(orderType || "DESC").toUpperCase();
  const isCustomOrder = normalizedOrder === "CUSTOM";
  const { data, error } = await supabase.rpc("get_custom_content_orderedd", {
    userid: userId,
    user_name: username.trim().toLowerCase(),
    post_ids: ids,
    order_type: isCustomOrder ? "CUSTOM" : normalizedOrder || "DESC",
    custom_order: isCustomOrder
      ? customOrder?.length
        ? customOrder
        : ids
      : null,
    platforms,
    limit_num: limit,
    removed_post_ids: removedPostIds,
  });
  if (error) throw error;
  return rpcRows(data);
}

/** Same analytics + post-id load path Stay Up To Date uses for trending feeds. */
async function fetchTrendingPosts(args: {
  userId: string;
  username: string;
  details?: string;
  kind: "content" | "products";
  orderType?: string;
  customOrder?: string[];
  limit: number;
  componentId?: string | number | null;
  from?: string | null;
  query?: string | null;
  postIds?: string | null;
  /** Match Add ghost preview — always recompute from analytics params. */
  bypassSnapshot?: boolean;
}): Promise<unknown[]> {
  const {
    userId,
    username,
    kind,
    orderType = "DESC",
    customOrder = [],
    limit,
    componentId,
    from,
    query,
    postIds: componentPostIds,
  } = args;
  const details =
    String(args.details || "").trim() || buildTrendingDetails("Last 7 days", 1);
  if (kind === "content") {
    const loaded = await loadTrendingContentEditorPostIds({
      userId,
      username,
      details,
    });
    const fetchIds = trendingContentHydrateIds(loaded.windows, limit);
    if (!fetchIds.length) return [];
    const { data: searchResult, error: searchErr } = await supabase.rpc(
      "search_postids_content_with_countor",
      {
        userid: userId,
        username,
        postids: fetchIds,
        socials: StaticVariables().CONTENT_PLATFORMS,
        max_count: fetchIds.length,
        post_order: orderType,
        // A leftover custom sequence makes Newest/Oldest return the custom list.
        custom_order:
          String(orderType || "").toUpperCase() === "CUSTOM" ? customOrder : [],
      },
    );
    if (searchErr) throw searchErr;
    const posts = (searchResult as { posts?: unknown[] } | null)?.posts;
    const rows = sortFeedRowsByPostDate(
      Array.isArray(posts) ? posts : rpcRows(searchResult),
      orderType,
    );
    return trendingContentPostsForRange(rows, loaded.windows, limit, (raw) =>
      raw && typeof raw === "object"
        ? String((raw as { post_id?: unknown }).post_id || "").trim()
        : "",
    );
  }
  const { clickNumber, startDate, endDate } =
    resolveTrendingAnalyticsDates(details);
  if (!userId || !username || !startDate || !endDate) return [];

  const typeLabel = "trending products";
  const { postIds: trendingIds } = await getOrComputeTrendingPostIds({
    params: {
      userId,
      username,
      numberOfClicks: clickNumber,
      startDate,
      endDate,
      type: typeLabel,
      // Website shop clicks (template_analytics), same source as Top shopped.
      clickSource: "template-shop",
    },
    persist: {
      componentId,
      from: from || "website-template",
      query,
      postIds: componentPostIds,
    },
    bypassSnapshot: args.bypassSnapshot ?? false,
    label: `FeaturedTrending:${typeLabel}`,
  });
  if (!trendingIds.length) return [];

  const { data: searchResult, error: searchErr } = await supabase.rpc(
    "searchresulltte_with_countor",
    {
      userid: userId,
      username,
      postids: trendingIds,
      max_count: Math.max(limit * 2, 25),
      post_order: orderType,
      custom_order:
        String(orderType || "").toUpperCase() === "CUSTOM" ? customOrder : [],
    },
  );
  if (searchErr) throw searchErr;
  const posts = (searchResult as { posts?: unknown[] } | null)?.posts;
  const rows = Array.isArray(posts) ? posts : rpcRows(searchResult);
  const productPlatforms = new Set(
    StaticVariables().PRODUCT_PLATFORMS.map((p: string) =>
      String(p).toLowerCase(),
    ),
  );
  productPlatforms.add("misc-product");
  const productOnly = rows.filter((raw) => {
    if (!raw || typeof raw !== "object") return false;
    const row = raw as Record<string, unknown>;
    const shop = String(row.shop_link ?? "").trim();
    if (!shop) return false;
    const platform = String(row.platform ?? "")
      .trim()
      .toLowerCase();
    return Boolean(platform) && productPlatforms.has(platform);
  });
  return productOnly.slice(0, limit);
}

async function fetchPlatformTypePosts(args: {
  userId: string;
  username: string;
  platform: string;
  feedTypes: string[];
  orderType?: string;
  customOrder?: string[];
  shopFilter?: string;
  limit: number;
  dateWindow?: ContentFeedDateWindow | null;
}): Promise<unknown[]> {
  const {
    userId,
    username,
    platform,
    feedTypes: rawFeedTypes,
    orderType = "DESC",
    customOrder = [],
    shopFilter = "all",
    limit,
    dateWindow,
  } = args;
  const lcUser = username.trim().toLowerCase();
  const types = rawFeedTypes.length ? rawFeedTypes : ["REELS"];
  const normalizedOrder = normalizeFeedOrderType(orderType);
  const applyDate = Boolean(dateWindow?.applyFilter);
  const windowStart = dateWindow?.startDate || null;
  const windowEnd = dateWindow?.endDate || null;
  const windowComponentType = dateWindow?.componentType || null;
  const windowSelectedDates = dateWindow?.selectedDates || null;

  // By the Day with no weekdays chosen should preview empty, not the full feed.
  if (
    applyDate &&
    windowComponentType === "daywise" &&
    (!windowSelectedDates || windowSelectedDates.length === 0)
  ) {
    return [];
  }

  if (platform === "all" || types.some((t) => t.toUpperCase() === "ALL")) {
    const allBase = {
      userid: userId,
      filter: [...CONTENT_FEED_ALL_FEED_TYPES],
      query_str: "",
      user_name: lcUser,
      socials: [...CONTENT_FEED_ALL_SOCIALS],
      order_type: normalizedOrder,
      custom_order: customOrder,
      limit_num: limit,
      offset_num: 0,
      pinned_post_ids: [] as string[],
      shoppable_status_filter: shopFilter,
    };
    const { data, error } = applyDate
      ? await supabase.rpc("get_content_all_platforms_dated", {
          ...allBase,
          start_date: windowStart,
          end_date: windowEnd,
          component_type: windowComponentType,
          selected_dates: windowSelectedDates,
          user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        })
      : await supabase.rpc("get_content_all_platforms_new", allBase);
    if (error) throw error;
    return rpcRows(data);
  }

  if (types.some((t) => t.toUpperCase() === "STORY")) {
    const today = dayjs().utc().format("YYYY-MM-DD");
    const yesterday = dayjs().utc().subtract(7, "day").format("YYYY-MM-DD");
    const start = applyDate && windowStart ? windowStart : yesterday;
    const end = applyDate && windowEnd ? windowEnd : today;
    const useDatedStoryRpc = applyDate;
    const storyParams = useDatedStoryRpc
      ? {
          userid: userId,
          user_name: lcUser,
          platform_name: platform,
          feed_type: "STORY",
          start_date: start,
          end_date: end,
          order_type: normalizedOrder,
          custom_order: customOrder,
          limit_num: limit,
          offset_num: 0,
          pinned_post_ids: [] as string[],
          shoppable_status_filter: shopFilter,
          component_type: windowComponentType,
          selected_dates: windowSelectedDates,
          user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }
      : {
          userid: userId,
          user_name: lcUser,
          platform_name: platform,
          feed_type: "STORY",
          start_date: start,
          end_date: end,
          order_type: normalizedOrder,
          custom_order: customOrder,
          limit_num: limit,
          offset_num: 0,
          pinned_post_ids: [] as string[],
          shoppable_status_filter: shopFilter,
        };
    const storyOnly = types.length === 1 && types[0].toUpperCase() === "STORY";
    const { data: storyData, error: storyError } = await supabase.rpc(
      useDatedStoryRpc
        ? "get_storytypecontenttteonord_pin_dated"
        : "get_storytypecontenttteonord_pin_new",
      storyParams,
    );
    if (storyError) throw storyError;
    const storyRows = sortFeedRowsByPostDate(
      rpcRows(storyData),
      normalizedOrder,
    );
    // Stories-only Show — dedicated story RPC.
    if (storyOnly) return filterRowsByRequestedFeedTypes(storyRows, types);

    // Instagram Show "All" — merge posts/reels with stories.
    const nonStoryTypes = types
      .map((t) => t.toUpperCase())
      .filter((t) => t !== "STORY");
    const feed_type =
      platform.toLowerCase() === "linkedin" ? ["POST"] : nonStoryTypes;
    const baseParams = {
      userid: userId,
      user_name: lcUser,
      platform_name: platform,
      feed_type,
      order_type: normalizedOrder,
      custom_order: customOrder,
      limit_num: limit,
      offset_num: 0,
      pinned_post_ids: [] as string[],
      shoppable_status_filter: shopFilter,
    };
    const { data, error } = applyDate
      ? await supabase.rpc("get_content_type_postsspinn_dated", {
          ...baseParams,
          start_date: windowStart,
          end_date: windowEnd,
          component_type: windowComponentType,
          selected_dates: windowSelectedDates,
          user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        })
      : await supabase.rpc("get_content_type_postsspinn_new", baseParams);
    if (error) throw error;
    const otherRows = rpcRows(data);
    const seen = new Set<string>();
    const merged: unknown[] = [];
    for (const row of [...otherRows, ...storyRows]) {
      const id = String(
        (row as { id?: unknown; post_id?: unknown })?.id ??
          (row as { post_id?: unknown })?.post_id ??
          "",
      ).trim();
      if (id && seen.has(id)) continue;
      if (id) seen.add(id);
      merged.push(row);
    }
    return sortFeedRowsByPostDate(merged, normalizedOrder).slice(0, limit);
  }

  const feed_type =
    platform.toLowerCase() === "linkedin"
      ? ["POST"]
      : types.map((t) => t.toUpperCase());
  const baseParams = {
    userid: userId,
    user_name: lcUser,
    platform_name: platform,
    feed_type,
    order_type: normalizedOrder,
    custom_order: customOrder,
    limit_num: limit,
    offset_num: 0,
    pinned_post_ids: [] as string[],
    shoppable_status_filter: shopFilter,
  };
  const { data, error } = applyDate
    ? await supabase.rpc("get_content_type_postsspinn_dated", {
        ...baseParams,
        start_date: windowStart,
        end_date: windowEnd,
        component_type: windowComponentType,
        selected_dates: windowSelectedDates,
        user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      })
    : await supabase.rpc("get_content_type_postsspinn_new", baseParams);
  if (error) throw error;
  return filterRowsByRequestedFeedTypes(rpcRows(data), feed_type);
}

/** Latest Product Feed — same featured RPC as LiB Reels products path, scoped by Show. */
async function fetchShowScopeLatestPosts(args: {
  userId: string;
  username: string;
  orderType?: string;
  customOrder?: string[];
  shoppableContentFilter?: string;
  shopFilter?: string;
  limit: number;
}): Promise<unknown[]> {
  const {
    userId,
    username,
    orderType = "DESC",
    customOrder = [],
    shoppableContentFilter,
    shopFilter = "all",
    limit,
  } = args;
  const lcUser = username.trim().toLowerCase();
  const showFilter = asShowFilter(shoppableContentFilter, "products");
  const socials = deriveShowPlatforms(showFilter, true).filter(Boolean);
  const { data, error } = await supabase.rpc("get_featuredddataaeeord_pin_new", {
    userid: userId,
    filter: ["FEED", "REELS", "STORY", "HIGHLIGHTS", "PIN", "VIDEO", "PRODUCT"],
    query_str: "",
    user_name: lcUser,
    socials,
    order_type: orderType,
    custom_order: customOrder,
    limit_num: limit,
    offset_num: 0,
    pinned_post_ids: [],
    shoppable_status_filter: shopFilter,
  });
  if (error) throw error;
  return rpcRows(data);
}

async function fetchQueryBasedPosts(args: {
  userId: string;
  username: string;
  queryTerm: string;
  orderType?: string;
  customOrder?: string[];
  shoppableContentFilter?: string;
  details?: unknown;
  /** Product keyword / query-links → query_based_shop. */
  isShopLinks?: boolean;
  limit: number;
}): Promise<unknown[]> {
  const {
    userId,
    username,
    queryTerm,
    orderType = "DESC",
    customOrder = [],
    shoppableContentFilter,
    details,
    limit,
  } = args;
  const lcUser = username.trim().toLowerCase();
  const sv = StaticVariables();
  const normalizedQuery = queryTerm.toLowerCase().replace(/\s+/g, " ").trim();
  if (!normalizedQuery || !userId || !lcUser) return [];

  const platformFilter = readPlatformFilterFromDetails(details);
  const showFilter = (
    (shoppableContentFilter || "both") as string
  ).toLowerCase() as ShowFilter;
  // Caller sets isShopLinks from type (query-links only). Do not upgrade to
  // shop RPC from a stale shoppable_content_filter=products on content keywords.
  const isShopLinks = Boolean(args.isShopLinks);
  const showSocials = deriveAllowedPlatforms(
    platformFilter,
    // Content keyword RPC path: never pass products show-scope into platforms.
    isShopLinks ? showFilter : showFilter === "products" ? "content" : showFilter,
    isShopLinks,
  );

  let words = normalizedQuery.split(" ");
  // Live QueryContentDataFront always ranks with DEFAULT_CONTENT_ORDER
  // ("Time Based" → "time"); ASC/DESC goes in post_order only.
  let localContentOrder = String(
    sv.DEFAULT_CONTENT_ORDER || "Time Based",
  );
  if (
    words.length === 1 &&
    !(sv.SEARCH_EXCEPTIONS as string[]).includes(normalizedQuery)
  ) {
    const suffixRegex = /(s)$/i;
    if (suffixRegex.test(words[0])) {
      words = [words[0].replace(suffixRegex, "")];
    }
  }
  if (words.includes("shop") || words.includes("buy")) {
    localContentOrder = "shoppable";
    words = words.filter((w) => w !== "shop" && w !== "buy");
  }
  const filteredWords = words.filter(
    (word: string) =>
      !(sv.connectingWords as string[]).includes(word.toLowerCase()),
  );
  const joined = filteredWords.join(" ");
  const pgQuery = joined.toLowerCase().replace(/\s+/g, " & ");
  const singlePatterns = buildPhraseIlikePatterns(normalizedQuery);
  const localOrder = localContentOrder.split(" ")[0].toLowerCase();
  const rpcName = isShopLinks ? "query_based_shop" : "query_based_cont";

  const { data: searchResult, error } = await supabase.rpc(rpcName, {
    query_string: pgQuery,
    userid: userId,
    username: lcUser,
    socials: showSocials,
    content_order: localOrder,
    single: singlePatterns,
    post_order: String(orderType || "DESC"),
    custom_order: customOrder,
    limit_num: limit,
    offset_num: 0,
    removed_post_ids: [],
  });
  if (error) throw error;
  return filterPosts(
    filterOutProductCatalogPosts(rpcRows(searchResult), isShopLinks),
    platformFilter,
    showFilter,
    isShopLinks,
  );
}

function resolveTypeFeed(
  carousel: CarouselData,
): { platform: string; feedTypes: string[] } | null {
  const typeLc = String(carousel.componentType || "")
    .trim()
    .toLowerCase();
  const displayLc = String(carousel.componentDisplayName || "")
    .trim()
    .toLowerCase();

  // Custom picks store the title in display_name — never treat that as a
  // platform Latest feed (e.g. title "linkedin" → LinkedIn RPC).
  if (
    typeLc === "content" ||
    typeLc === "links" ||
    typeLc === "shoppable"
  ) {
    return null;
  }

  // Story type wins over a stale display_name of "reels". That stale name
  // was loading Posts & Reels (and Show All) while the control said Stories.
  if (typeLc === "instagram story" || displayLc === "instagram story") {
    return { platform: "instagram", feedTypes: ["STORY"] };
  }

  if (typeLc === "reels" || displayLc === "reels") {
    return {
      platform: "instagram",
      feedTypes: mapReelsFilterToFeedTypes(carousel.shoppableContentFilter),
    };
  }

  const mapped =
    TYPE_TO_PLATFORM_FEED[typeLc] || TYPE_TO_PLATFORM_FEED[displayLc] || null;
  // Banner Display as → latest-podcast (single episode); still a podcast feed.
  if (
    !mapped &&
    (typeLc === "latest-podcast" || displayLc === "latest-podcast")
  ) {
    return { platform: "podcast", feedTypes: ["PODCAST"] };
  }
  if (!mapped) return null;
  return { platform: mapped.platform, feedTypes: [mapped.feedType] };
}

/** Days for automated day-window tabs (This Week Content = 7, etc.). */
function resolveDayWindowDays(carousel: CarouselData): number | null {
  const typeLc = String(carousel.componentType || "")
    .trim()
    .toLowerCase();
  const displayLc = String(carousel.componentDisplayName || "")
    .trim()
    .toLowerCase();
  const titleLc = String(carousel.title || "")
    .trim()
    .toLowerCase();

  // content-feed platform rows (reels/all/tiktok/…) date via details timeframe
  // (getContentFeedDateWindowFromComponent). Never hijack on title keywords
  // like "Shop this past week" — that wrongly routes to product shoplink RPCs.
  if (isPlatformContentFeedType(typeLc) && !hasProductFeedKind(carousel.details)) {
    return null;
  }

  // Explicit day-window component types / display names (product or content).
  if (
    displayLc === "this week content" ||
    displayLc === "this week" ||
    typeLc === "this week"
  ) {
    return 7;
  }
  if (displayLc === "yesterday" || typeLc === "yesterday") return 1;
  if (displayLc === "this month" || typeLc === "this month") return 30;

  // Title heuristics only for legacy empty-type / daywise product collections —
  // not for named content-feed strips.
  if (
    titleLc === "this week's content" ||
    titleLc === "this week content" ||
    titleLc.includes("this past week") ||
    titleLc.includes("past week") ||
    (titleLc.includes("this week") && !titleLc.includes("month"))
  ) {
    return 7;
  }
  if (
    titleLc.includes("this month") ||
    titleLc.includes("trending this month")
  ) {
    return 30;
  }

  if (typeLc === "daywise" || displayLc === "daywise") {
    const raw = carousel.details;
    if (typeof raw === "string" && raw.trim()) {
      try {
        const parsed = JSON.parse(raw) as unknown;
        if (Array.isArray(parsed) && parsed.length) {
          const n = Number(parsed[0]);
          if (Number.isFinite(n) && n > 0) return n;
        }
      } catch {
        const n = Number(raw);
        if (Number.isFinite(n) && n > 0) return n;
      }
    }
  }
  return null;
}

async function fetchDayWindowPosts(args: {
  userId: string;
  username: string;
  days: number;
  orderType?: string;
  customOrder?: string[];
  shoppableContentFilter?: string;
  /** When true, always use content RPC (matches website Content destination). */
  preferContentRpc?: boolean;
  removedPostIds?: string[];
  limit: number;
}): Promise<unknown[]> {
  const {
    userId,
    username,
    days,
    orderType = "DESC",
    customOrder = [],
    shoppableContentFilter,
    preferContentRpc = false,
    removedPostIds = [],
    limit,
  } = args;
  const lcUser = username.trim().toLowerCase();
  // LiB day-windows default to BOTH platforms when filter is empty — not products.
  const showFilter = asShowFilter(
    shoppableContentFilter,
    preferContentRpc ? "content" : "both",
  );
  const socials = deriveShowPlatforms(showFilter, true).filter(Boolean);
  const removed = Array.isArray(removedPostIds)
    ? removedPostIds.map(String).filter(Boolean)
    : [];

  // Match LiB YesterdayLinksLoader / useLoadYesterdayLinks:
  // Yesterday / This Week / This Month always use get_shoplink_content;
  // shoppable_content_filter only picks CONTENT vs PRODUCT vs BOTH socials.
  // Only display_name "This Week Content" (preferContentRpc) uses get_yesterday_content.
  if (!preferContentRpc) {
    const windowRange = computeWindow([days]);
    if (!windowRange.start || !windowRange.end) return [];
    const startDate = windowRange.start.split(" ")[0];
    const endDate = windowRange.end.split(" ")[0];
    const { data: searchResult, error } = await supabase.rpc(
      "get_shoplink_content",
      {
        userid: userId,
        user_name: lcUser,
        start_date: startDate,
        end_date: endDate,
        post_ids: [],
        order_type: orderType || "DESC",
        custom_order: customOrder,
        socials,
        limit_num: limit,
        offset_num: 0,
        removed_post_ids: removed,
        component_type: null,
        selected_dates: [],
        user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    );
    if (error) throw error;
    const rows = (searchResult as { data?: unknown } | null)?.data ?? searchResult;
    return Array.isArray(rows) ? rows : rpcRows(rows);
  }

  const numDayStart = dayjs()
    .utc()
    .subtract(Math.max(1, days), "day")
    .startOf("day")
    .format("YYYY-MM-DD HH:mm:ss");
  const numDayEnd = dayjs().utc().format("YYYY-MM-DD HH:mm:ss");

  const { data: result, error } = await supabase.rpc("get_yesterday_content", {
    userid: userId,
    user_name: lcUser,
    start_date: numDayStart,
    end_date: numDayEnd,
    post_ids: [],
    order_type: orderType,
    content_platforms: socials,
    custom_order: customOrder,
    limit_num: limit,
    offset_num: 0,
    removed_post_ids: removed,
  });
  if (error) throw error;
  return rpcRows(result);
}

/** Product By the Day — same get_shoplink_content daywise path as LiB. */
async function fetchProductDayWisePosts(args: {
  userId: string;
  username: string;
  selectedDays: string[];
  orderType?: string;
  customOrder?: string[];
  removedPostIds?: string[];
  limit: number;
}): Promise<unknown[]> {
  const {
    userId,
    username,
    selectedDays,
    orderType = "DESC",
    customOrder = [],
    removedPostIds = [],
    limit,
  } = args;
  const window = getProductDayWiseShoplinkWindow(selectedDays);
  if (!window) return [];
  const lcUser = username.trim().toLowerCase();
  const socials = StaticVariables().PRODUCT_PLATFORMS;
  const removed = Array.isArray(removedPostIds)
    ? removedPostIds.map(String).filter(Boolean)
    : [];
  const { data: searchResult, error } = await supabase.rpc(
    "get_shoplink_content",
    {
      userid: userId,
      user_name: lcUser,
      start_date: window.startDate,
      end_date: window.endDate,
      post_ids: [],
      order_type: orderType || "DESC",
      custom_order: customOrder,
      socials,
      limit_num: limit,
      offset_num: 0,
      removed_post_ids: removed,
      component_type: "daywise",
      selected_dates: window.selectedDates,
      user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  );
  if (error) throw error;
  const rows = (searchResult as { data?: unknown } | null)?.data ?? searchResult;
  return Array.isArray(rows) ? rows : rpcRows(rows);
}

/** Load the top posts for one Recent Content (`template_components`) tab. */
export async function fetchFeaturedCarouselPostsForTab(args: {
  userId: string;
  username: string;
  carousel: CarouselData;
  limit?: number;
  bypassTrendingSnapshot?: boolean;
}): Promise<FeaturedCarouselItem[]> {
  const {
    userId,
    username,
    carousel,
    limit = FEATURED_CAROUSEL_POST_LIMIT,
    bypassTrendingSnapshot = false,
  } = args;
  const tabSlug = String(carousel.renderSlug || carousel.id || "").trim();
  if (!tabSlug || !userId || !username) return [];

  const postIds = parseIdList(carousel.postIds);
  const orderType = normalizeFeedOrderType(carousel.contentOrder);
  const customOrder = parseIdList(carousel.customOrder);
  const typeLc = String(carousel.componentType || "")
    .trim()
    .toLowerCase();
  const queryTerm = String(carousel.query || "").trim();
  const shopFilter = resolvePreviewShoppableFilter(carousel);
  const removedPostIds = parseRemovedPostIds(carousel);
  const preparePosts = (posts: unknown[]) =>
    filterPostsByShoppableStatus(
      excludeRemovedPosts(posts, removedPostIds),
      shopFilter,
    );
  // Podcast Banner (latest-podcast) always shows a single episode.
  const isLatestPodcast =
    typeLc === "latest-podcast" ||
    String(carousel.componentDisplayName || "")
      .trim()
      .toLowerCase() === "latest-podcast";
  const effectiveLimit = isLatestPodcast ? 1 : limit;
  // Modest headroom so imageless skips can still fill `limit`. Keep ASC/DESC on
  // the same RPC window size so Order only changes sort, not a tiny alternate set.
  const stripFetchLimit = Math.max(effectiveLimit * 2, effectiveLimit);

  const finish = async (items: FeaturedCarouselItem[]) => {
    if (!isLatestPodcast) return items;
    return backfillOutboundUrls(items, username, userId);
  };

  // Word / phrase — before post_ids. A stored id list must not replace the
  // phrase match set the custom-order picker is arranging.
  if (
    (typeLc === "content-series" ||
      typeLc === "query-content" ||
      typeLc === "query-links") &&
    queryTerm
  ) {
    const isShopLinks = shouldUseShopKeywordRpc(
      typeLc,
      carousel.shoppableContentFilter,
    );
    const isCustom = orderType.toUpperCase() === "CUSTOM";
    // Always page the keyword search newest-first. ASC changes which rows
    // come back, and the product filter then drops that older page — the
    // strip looks empty. Newest/Oldest is applied to this same set below.
    const posts = await fetchQueryBasedPosts({
      userId,
      username,
      queryTerm,
      orderType: "DESC",
      customOrder: [],
      shoppableContentFilter: carousel.shoppableContentFilter,
      details: carousel.details,
      isShopLinks,
      limit: isCustom ? 100 : Math.max(limit * 3, limit),
    });
    const ordered = isCustom
      ? sortRowsByCustomOrder(posts, customOrder)
      : String(orderType || "").toUpperCase() === "ASC"
        ? sortFeedRowsByPostDate(posts, "ASC")
        : posts;
    return finish(
      mapPosts(
        preparePosts(ordered),
        tabSlug,
        effectiveLimit,
        isShopLinks ? { forceProduct: true, kind: "shop" } : undefined,
      ),
    );
  }

  // Featured is a live highlight feed (same RPC as add/edit Featured modals).
  // Handle it before `post_ids` so a stored id list is not loaded as custom content.
  if (typeLc === "featured") {
    const lcUser = username.trim().toLowerCase();
    const { data, error } = await supabase.rpc(
      "get_featuredddataaeeord_pin_new",
      {
        userid: userId,
        filter: [
          "FEED",
          "REELS",
          "STORY",
          "HIGHLIGHTS",
          "PIN",
          "VIDEO",
          "PODCAST",
          "TIKTOK",
          "MAIL",
          "TWEET",
          "PRODUCT",
          "POST",
        ],
        query_str: "",
        user_name: lcUser,
        socials: [
          "instagram",
          "youtube",
          "tiktok",
          "podcast",
          "pinterest",
          "newsletter",
          "twitter",
          "LTK",
          "Amazon",
          "ShopMy",
          "MISC",
          "linkedin",
          "MISC-content",
        ],
        order_type: orderType,
        custom_order:
          String(orderType || "").toUpperCase() === "CUSTOM" ? customOrder : [],
        limit_num: Math.max(limit * 3, limit),
        offset_num: 0,
        pinned_post_ids: [],
        shoppable_status_filter: shopFilter,
      },
    );
    if (error) throw error;
    const featuredRows =
      String(orderType || "").toUpperCase() === "CUSTOM"
        ? sortRowsByCustomOrder(rpcRows(data), customOrder)
        : rpcRows(data);
    return finish(
      mapPosts(preparePosts(featuredRows), tabSlug, effectiveLimit),
    );
  }

  // Trending: `post_ids` are a daily analytics snapshot, not a curated pick.
  // Handle before the hand-picked `postIds` short-circuit so View All reloads
  // the full trending set (LiB strip uses the same analytics path).
  if (typeLc === "trending content" || typeLc === "trending products") {
    const isProducts = typeLc === "trending products";
    const posts = await fetchTrendingPosts({
      userId,
      username,
      details: carousel.details,
      kind: isProducts ? "products" : "content",
      orderType,
      customOrder,
      // fetchTrendingPosts already applies 2× max_count headroom — do not
      // pass stripFetchLimit (that was 3× then 2× again).
      limit: effectiveLimit,
      // `carousel.id` is the display/tab name — DB PK lives on stayComponentRowId.
      componentId: carousel.stayComponentRowId || null,
      from: "website-template",
      query: carousel.query,
      postIds:
        parseIdList(
          carousel.postIds ??
            (carousel as { post_ids?: unknown }).post_ids,
        ).join(",") || null,
      bypassSnapshot: bypassTrendingSnapshot,
    });
    return finish(
      mapPosts(
        preparePosts(posts),
        tabSlug,
        effectiveLimit,
        isProducts ? { forceProduct: true, kind: "shop" } : undefined,
      ),
    );
  }

  // Instagram Show must query live. A stored id list is the old Show All
  // snapshot. Other platforms, product feeds, and Custom order keep post ids.
  const liveInstagramShow =
    (typeLc === "reels" || typeLc === "instagram story") &&
    String(orderType || "").toUpperCase() !== "CUSTOM" &&
    !isProductScopeLatest(carousel);
  if (postIds.length > 0 && !liveInstagramShow) {
    const isProductPick =
      typeLc === "links" ||
      String(carousel.shoppableContentFilter || "")
        .trim()
        .toLowerCase() === "products";
    const posts = await fetchPostsByIds({
      userId,
      username,
      postIds,
      orderType:
        orderType.toUpperCase() === "CUSTOM" ? "CUSTOM" : orderType || "DESC",
      customOrder:
        orderType.toUpperCase() === "CUSTOM"
          ? customOrder.length
            ? customOrder
            : postIds
          : null,
      limit: Math.max(limit * 3, limit),
      removedPostIds,
      shoppableContentFilter: carousel.shoppableContentFilter,
      preferProductPlatforms: isProductPick,
    });
    let prepared = excludeRemovedPosts(posts, removedPostIds);
    if (!isProductPick && shopFilter !== "all") {
      prepared = filterPostsWithShopLinks(
        prepared,
        shopFilter,
        await fetchShopLinkMap(
          username,
          userId,
          prepared.map((raw) =>
            raw && typeof raw === "object"
              ? (raw as { post_id?: unknown }).post_id
              : "",
          ),
        ),
      );
    } else {
      prepared = filterPostsByShoppableStatus(prepared, shopFilter);
    }
    // Custom products only. Newest/Oldest use the shared date sort.
    // A saved custom arrangement keeps its sequence.
    if (typeLc === "links") {
      prepared =
        orderType.toUpperCase() === "CUSTOM" && customOrder.length
          ? sortRowsByCustomOrder(prepared, customOrder)
          : sortFeedRowsByPostDate(
              prepared,
              orderType.toUpperCase() === "ASC" ? "ASC" : "DESC",
            );
    }
    return finish(
      mapPosts(
        prepared,
        tabSlug,
        effectiveLimit,
        isProductPick ? { forceProduct: true, kind: "shop" } : undefined,
      ),
    );
  }

  // Product By the Day — weekday names live in display_name (LiB Recent Products).
  if (typeLc === "daywise") {
    const filterLc = String(carousel.shoppableContentFilter || "")
      .trim()
      .toLowerCase();
    const isProductDaywise =
      filterLc === "products" || hasProductFeedKind(carousel.details);
    if (isProductDaywise) {
      const selectedDays = parseDayWiseSelectedDays({
        type: carousel.componentType,
        display_name: carousel.componentDisplayName,
      });
      if (selectedDays.length) {
        const posts = await fetchProductDayWisePosts({
          userId,
          username,
          selectedDays,
          orderType,
          customOrder,
          removedPostIds,
          limit: stripFetchLimit,
        });
        return finish(
          mapPosts(preparePosts(posts), tabSlug, effectiveLimit, {
            forceProduct: true,
            kind: "shop",
          }),
        );
      }
    }
  }

  // This Week / Yesterday / This Month day-windows (and empty-type
  // "This Week Content" rows) — same RPC as destination loaders.
  const dayWindow = resolveDayWindowDays(carousel);
  if (dayWindow != null) {
    const displayLc = String(carousel.componentDisplayName || "")
      .trim()
      .toLowerCase();
    // Match classic LiB: only "This Week Content" uses get_yesterday_content.
    // filter=content on Yesterday / This Week / This Month is a platform
    // selector for get_yesterday_links, not a content-carousel type.
    const preferContentRpc = displayLc === "this week content";
    const posts = await fetchDayWindowPosts({
      userId,
      username,
      days: dayWindow,
      orderType,
      customOrder,
      shoppableContentFilter: carousel.shoppableContentFilter,
      preferContentRpc,
      removedPostIds,
      // Day-window RPCs (esp. 30-day shoplink) are expensive — keep modest
      // imageless headroom (2×) instead of the old 3× over-fetch.
      limit: stripFetchLimit,
    });
    const dayShowFilter = asShowFilter(
      carousel.shoppableContentFilter,
      preferContentRpc ? "content" : "both",
    );
    const prepared = preparePosts(
      preferContentRpc || dayShowFilter !== "products"
        ? excludeProductTypeRows(posts, carousel.shoppableContentFilter)
        : posts,
    );
    // Product-bank day windows only — content/both keep feed post framing
    // (same posts LiB shows for "Shop This Past Week").
    return finish(
      mapPosts(
        prepared,
        tabSlug,
        effectiveLimit,
        !preferContentRpc && dayShowFilter === "products"
          ? { forceProduct: true, kind: "shop" }
          : undefined,
      ),
    );
  }

  const feed = resolveTypeFeed(carousel);
  if (isProductScopeLatest(carousel)) {
    const showFilter = asShowFilter(carousel.shoppableContentFilter, "products");
    const posts = await fetchShowScopeLatestPosts({
      userId,
      username,
      orderType,
      customOrder,
      shoppableContentFilter: carousel.shoppableContentFilter,
      shopFilter,
      limit: Math.max(limit * 3, limit),
    });
    const scoped =
      showFilter === "products"
        ? posts
        : excludeProductTypeRows(posts, carousel.shoppableContentFilter);
    return finish(
      mapPosts(
        preparePosts(scoped),
        tabSlug,
        effectiveLimit,
        showFilter === "products" ? { forceProduct: true, kind: "shop" } : undefined,
      ),
    );
  }
  if (feed) {
    const dateWindow = getContentFeedDateWindowFromComponent({
      details: carousel.details,
      id: carousel.stayComponentRowId || carousel.id,
    });
    const posts = await fetchPlatformTypePosts({
      userId,
      username,
      platform: feed.platform,
      feedTypes: feed.feedTypes,
      orderType,
      customOrder,
      shopFilter,
      limit: dateWindow.applyFilter
        ? Math.max(stripFetchLimit, effectiveLimit * 3)
        : stripFetchLimit,
      dateWindow,
    });
    const isProducts =
      String(carousel.shoppableContentFilter || "")
        .trim()
        .toLowerCase() === "products";
    const prepared = preparePosts(
      isProducts
        ? posts
        : excludeProductTypeRows(posts, carousel.shoppableContentFilter),
    );
    return finish(
      mapPosts(
        prepared,
        tabSlug,
        effectiveLimit,
        {
          ...(isProducts ? { forceProduct: true, kind: "shop" as const } : {}),
          // Podcast Banner still paints with title/CTA when art is missing;
          // other strips keep requiring thumbs so empty slots stay empty.
          requireImage: !isLatestPodcast,
        },
      ),
    );
  }

  if (typeLc === "videos" || typeLc === "content") {
    return [];
  }

  return [];
}

/** When strip RPCs omit permalink, read it from the creator content table. */
async function backfillOutboundUrls(
  items: FeaturedCarouselItem[],
  username: string,
  userId: string,
): Promise<FeaturedCarouselItem[]> {
  const need = items.filter(
    (it) =>
      !String(it.permalink || "").trim() &&
      !String(it.shopLink || "").trim() &&
      it.id &&
      !String(it.id).startsWith("pending-"),
  );
  if (!need.length) return items;
  const lcUser = username.trim().toLowerCase();
  const ids = need.map((it) => it.id);
  const { data, error } = await supabase
    .from(lcUser)
    .select(
      "post_id, permalink, url, external_link, shop_link, table_url, tagged_link, media_url",
    )
    .eq("user_id", userId)
    .in("post_id", ids);
  if (error || !data?.length) return items;
  const byId = new Map<string, string>();
  for (const row of data) {
    if (!row || typeof row !== "object") continue;
    const rec = row as Record<string, unknown>;
    const id = String(rec.post_id ?? "").trim();
    const url = resolvePostOutboundUrl(rec);
    if (id && url) byId.set(id, url);
  }
  if (!byId.size) return items;
  return items.map((it) => {
    const url = byId.get(it.id);
    if (!url) return it;
    return { ...it, permalink: url };
  });
}
